import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { useIntl } from 'react-intl';
import showdown from 'showdown';
import { Typography } from '@deeptrue-frontend/ui';
import Interpretation from './Interpretation/Interpretation';
import { InsightType, InterpretationType } from '../../types';
import { translateGroupName } from '../../utils';
import { Tabs } from '../../DefaultReports';

interface Props {
  insight: InsightType;
  changeTab: (tab: Tabs) => void;
  disableGoToResults?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2.5rem 0;
  border-bottom: 1px solid #e3e5e8;

  &:first-of-type {
    padding-top: 0;
  }
`;

const LearnMore = styled(Typography)`
  cursor: pointer;
`;

const MarkDownWrapper = styled.span`
  p {
    margin: 0;
  }
`;

const Insight = ({ insight, changeTab, disableGoToResults }: Props) => {
  const [isExpanded, setExpanded] = useState(false);
  const { locale } = useIntl();
  const converter = new showdown.Converter();

  const { title, longDesc, shortDesc, interpretations } = insight;

  const titleWithMarkDown = converter.makeHtml(
    translateGroupName(title, locale)
  );
  const shortDescWithMarkDown = converter.makeHtml(
    translateGroupName(shortDesc, locale)
  );
  const longDescWithMarkDown = converter.makeHtml(
    translateGroupName(longDesc, locale)
  );

  const id = translateGroupName(title, locale);
  return (
    <Container id={id}>
      <Typography mb="1rem" fontSize="1.25rem" variant="h5" fontWeight="500">
        <MarkDownWrapper
          dangerouslySetInnerHTML={{ __html: titleWithMarkDown }}
        />
      </Typography>
      <Typography mb="1rem" fontSize="0.875rem" variant="h6" fontWeight="400">
        <MarkDownWrapper
          dangerouslySetInnerHTML={{ __html: shortDescWithMarkDown }}
        />
      </Typography>
      {isExpanded && (
        <Typography mb="1rem" fontSize="0.875rem" variant="h6" fontWeight="400">
          <MarkDownWrapper
            dangerouslySetInnerHTML={{ __html: longDescWithMarkDown }}
          />
        </Typography>
      )}
      <LearnMore
        mb="2.5rem"
        fontSize="0.875rem"
        variant="h6"
        fontWeight="700"
        color="#1656E9"
      >
        <span onClick={() => setExpanded(!isExpanded)}>
          {!isExpanded ? 'Learn more' : 'Hide'}
        </span>
      </LearnMore>
      {interpretations.map(
        (interpretation: InterpretationType, index: number) => (
          <Interpretation
            {...interpretation}
            key={index}
            changeTab={changeTab}
            disableGoToResults={disableGoToResults}
          />
        )
      )}
    </Container>
  );
};

export default Insight;
